import * as PIXI from 'pixi.js';
import moment from 'moment';
import { WIDTH, HEIGHT } from '@/constants';
import { Fx } from '@/audio/audio';
import { EVENT_BUS } from '@/model/event-bus';
import { bounce } from '@/util/tween';

export const style = new PIXI.TextStyle({
    fontFamily: 'Verdana',
    fontSize: 22,
    fill: ['#ffffff'],
    dropShadow: true,
    dropShadowBlur: 8,
    dropShadowColor: "#212121",
    align: 'center',
    wordWrapWidth: 440,
    lineJoin: 'round',
});

export const songItemStyle = new PIXI.TextStyle({
    ...style,
    align: 'left'
});

export function createText(config) {
    const richText = new PIXI.Text(config.text, {
        ...style,
        ...config
    });
    richText.anchor.set(0.5, 0.5);
    return richText;
}

export function createMenuItem({
    text = 'Button Text',
    xCoord = WIDTH / 2,
    yCoord = HEIGHT / 2,
    clickActionName = 'NO_ACTION',
    variation = '11',
    suppressMouseEvents = false,
    clickActionPayload = {},
    width = 200
} = {}) {
    const container = new PIXI.Container();
    const button = PIXI.Sprite.from(`public/images/ui-hires/Button${variation}.png`);
    const richText = new PIXI.Text(text, style);
    richText.anchor.set(0.5, 0.5);
    button.anchor.set(0.5, 0.5);
    richText.backGround = 0xFF00FF;
    button.interactive = true;
    button.buttonMode = true;

    /* SERIALIZED DATA FOR KEYBOARD NAVIGATION TO WORK */
    container.__meta = {
        clickActionName,
        clickActionPayload
    }

    if (!suppressMouseEvents) {
        button.on('pointerover', () => {
            EVENT_BUS.emit('MENU_HOVER', { sprite: container, mouseOver: true });
        });
        button.on('pointerout', () => {
            EVENT_BUS.emit('MENU_HOVER', { sprite: container, mouseOver: false });
        });
        button.on('pointerdown', (e) => {
            EVENT_BUS.emit('MENU_CLICK', {
                sprite: container,
                actionName: clickActionName,
                actionPayload: clickActionPayload
            });
        });
    }
    button.width = width;

    if (button.width < richText.width) {
        button.width = richText.width * 1.2;
    }

    container.addChild(button);
    container.addChild(richText);
    container.position.set(xCoord, yCoord);
    return container;
}

export function trunc(str = "TRUNC~no~str", charLimit = 10) {
    if (str.length > charLimit) {
        return str.substr(0, charLimit - 3) + '...';
    }
    return str;
}

export function createSongItem({
    title = 'Title',
    played = false,
    progress = 0,
    payload = {},
    coverArt = ''
}) {
    title = trunc(title, 36);
    const container = new PIXI.Container();
    const button = new PIXI.Sprite(PIXI.Texture.WHITE);
    button.tint = 0x333333;
    button.height = 80;

    const richText = new PIXI.Text(`${title}`, songItemStyle);
    richText.position.set(-580, -16);

    button.anchor.set(0.5, 0.5);
    button.interactive = true;
    button.buttonMode = true;

    /* SERIALIZED DATA FOR KEYBOARD NAVIGATION TO WORK */
    container.__meta = {
        clickActionName: 'SELECT_SONG',
        clickActionPayload: payload
    }

    button.on('pointerover', () => {
        EVENT_BUS.emit('MENU_HOVER', { sprite: button, mouseOver: true });
    });
    button.on('pointerout', () => {
        EVENT_BUS.emit('MENU_HOVER', { sprite: button, mouseOver: false });
    });
    button.on('pointerdown', (e) => {
        EVENT_BUS.emit('MENU_CLICK', {
            sprite: container,
            actionName: 'SELECT_SONG',
            actionPayload: payload
        });
    });

    button.width = 1200;
    container.addChild(button);
    container.addChild(richText);
    
    const statusText = new PIXI.Text(`Played: ${played ? moment(played).fromNow() : '⛔'} | ${(Number(progress) || 0).toFixed(2)}%`, songItemStyle);
    statusText.position.set(160, -18);
    container.addChild(statusText);
    return container;
}

export function createFolderItem({
    id = 'FOLDER_NAME',
    payload = {}
}) {
    const container = new PIXI.Container();
    const button = new PIXI.Sprite(PIXI.Texture.WHITE);
    button.tint = 0x333333;
    button.height = 80;

    const richText = new PIXI.Text(`${id}`, songItemStyle);
    richText.position.set(-200, -16);

    button.anchor.set(0.5, 0.5);
    button.interactive = true;
    button.buttonMode = true;

    /* SERIALIZED DATA FOR KEYBOARD NAVIGATION TO WORK */
    container.__meta = {
        clickActionName: 'SELECT_FOLDER',
        clickActionPayload: payload
    }

    button.on('pointerover', () => {
        EVENT_BUS.emit('MENU_HOVER', { sprite: button, mouseOver: true });
    });
    button.on('pointerout', () => {
        EVENT_BUS.emit('MENU_HOVER', { sprite: button, mouseOver: false });
    });
    button.on('pointerdown', (e) => {
        EVENT_BUS.emit('MENU_CLICK', {
            sprite: container,
            actionName: 'SELECT_FOLDER',
            actionPayload: payload
        });
    });

    button.width = 1200;
    container.addChild(button);
    container.addChild(richText);
    return container;
}

export function hover(button, isMouseOver) {
    button.tint = isMouseOver ? 0x00dd00 : 0x333333;
    if (isMouseOver) {
        Fx.click();
        // bounce(button).then(() => button.scale.set(1.15, 1.15));
        button.tint = 0x00ff00;
        button.isHighlighted = true;
    } else {
        // button.scale.set(1, 1);
        button.isHighlighted = false;
    }
}

export function createPanel({
    x = 0,
    y = 0,
    variation = '06',
    scale = 1,
    headerText = undefined
}) {
    const container = new PIXI.Container();

    const background = PIXI.Sprite.from(`public/images/ui-hires/Msg${variation}.png`);
    background.anchor.set(0.5, 0.5);
    container.addChild(background);
    background.scale.set(scale, scale)
    container.x = x - container.width / 2;
    container.y = y - container.height / 2;
    if (headerText) {
        const label = new PIXI.Text(headerText, style);
        label.y = -224;
        label.x = - label.width / 2;
        container.addChild(label);
    }
    return container;
}

export function createPopupPanel({
    x = WIDTH / 2,
    y = HEIGHT / 2,
    variation = '01',
    headerText = 'Popup Header',
    text = 'I am popup',
    onOK
}) {
    const container = new PIXI.Container();

    const background = PIXI.Sprite.from(`public/images/ui-hires/Msg${variation}.png`);
    background.anchor.set(0.5, 0.5);
    container.addChild(background);

    container.x = x - container.width / 2;
    container.y = y - container.height / 2;
    if (headerText) {
        const label = new PIXI.Text(headerText, style);
        label.y = -125;
        label.x = - label.width / 2;
        container.addChild(label);
    }

    if (text) {
        const label = createText({ text });
        label.y = -40;
        container.addChild(label);
    }

    if (onOK) {
        const button = PIXI.Sprite.from(`public/images/ui-hires/Button11.png`);
        const richText = new PIXI.Text(text, style);
        richText.anchor.set(0.5, 0.5);

        button.anchor.set(0.5, 0.5);
        button.scale.set(.7, .7);
        button.interactive = true;
        button.buttonMode = true;

        button.on('pointerdown', (e) => {
            onOK();
        });
        button.y = 85;
        container.addChild(button);
    }
    return container;
}

export class ScrollContainer extends PIXI.Container {
    scrollableContainer
    SCROLL_SPEED = 15

    constructor(width, height) {
        super();
        this.scrollableContainer = new PIXI.Container();
        this.width = width;
        this.height = height;

        this.addChild(this.scrollableContainer, false);
        const mask = new PIXI.Sprite(PIXI.Texture.WHITE);
        mask.width = width;
        mask.height = height;
        mask.anchor.set(0.5, 0.5);
        mask.position.set(WIDTH / 2, HEIGHT / 2);
        this.mask = mask;
        this.addChild(mask, false);
    }

    scrollUp() {
        this.scrollableContainer.y -= this.SCROLL_SPEED;
    }

    scrollDown() {
        this.scrollableContainer.y += this.SCROLL_SPEED;
    }

    addChild(child, overload = true) {
        if (!overload) {
            super.addChild(child)
        } else {
            this.scrollableContainer.addChild(child);
        }
    }

    removeChild(child) {
        this.scrollableContainer.removeChild(child);
    }
}